// App-only Google login: STEP 3 (page).
// When invoked with ?code=...&app=1 (Expo WebView), exchange the one-time
// handoff code for Supabase tokens, set the session, and land the user on
// the same logged-in destination as Apple/email login (/dashboard).
//
// Fallback (no code, or missing app flag): show a message to open the app.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/app-auth/google/consume")({
  head: () => ({
    meta: [
      { title: "Slutför inloggning – Alkoholprovet" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Consume,
});

function Consume() {
  const nav = useNavigate();
  const [status, setStatus] = useState<"working" | "fallback" | "error">("working");
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const isApp =
      params.get("app") === "1" ||
      (typeof navigator !== "undefined" && navigator.userAgent.includes("AlkoholprovetApp"));

    if (!code) {
      setStatus("error");
      setErrorMsg("Ogiltig inloggningslänk.");
      return;
    }

    if (!isApp) {
      // Not inside the app WebView — bounce to the app via deep link as fallback.
      const link = `alkoholprovet://auth/callback?code=${encodeURIComponent(code)}`;
      setDeepLink(link);
      setStatus("fallback");
      window.location.replace(link);
      return;
    }

    // Inside the app WebView: exchange the handoff code for tokens and sign in.
    (async () => {
      try {
        const res = await fetch("/api/public/app-auth/google/exchange", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });
        if (!res.ok) {
          setStatus("error");
          setErrorMsg("Inloggningen kunde inte slutföras. Försök igen.");
          return;
        }
        const data = (await res.json()) as {
          access_token?: string;
          refresh_token?: string;
          next?: string;
          cap?: string;
        };
        if (!data.access_token || !data.refresh_token) {
          setStatus("error");
          setErrorMsg("Inloggningen kunde inte slutföras. Försök igen.");
          return;
        }
        const { error } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
        if (error) {
          setStatus("error");
          setErrorMsg("Inloggningen kunde inte slutföras. Försök igen.");
          return;
        }
        // Clean the URL (remove code) and land on the standard logged-in destination.
        window.history.replaceState({}, "", "/app-auth/google/consume");
        // Route based on server-signed metadata ONLY. Never take next/cap from
        // an unsigned client field.
        if (data.next === "/pricing" && data.cap === "apple-v1") {
          window.location.assign("/pricing?app=1&native_iap=apple-v1");
          return;
        }
        nav({ to: "/dashboard", replace: true });
      } catch {
        setStatus("error");
        setErrorMsg("Inloggningen kunde inte slutföras. Försök igen.");
      }
    })();
  }, [nav]);

  return (
    <div className="container-narrow py-16 max-w-md text-center">
      <h1 className="font-display text-2xl font-bold">Slutför inloggning</h1>
      {status === "working" && (
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loggar in…
        </div>
      )}
      {status === "fallback" && (
        <>
          <p className="mt-3 text-sm text-muted-foreground">
            Öppnar Alkoholprovet-appen automatiskt… Om ingenting händer, tryck på knappen nedan.
          </p>
          {deepLink && (
            <a
              href={deepLink}
              className="mt-6 inline-flex items-center justify-center rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Öppna appen
            </a>
          )}
          <p className="mt-6 text-xs text-muted-foreground">
            Har du inte appen installerad? Stäng den här fliken och logga in via alkoholprovet.se istället.
          </p>
        </>
      )}
      {status === "error" && (
        <p className="mt-4 text-sm text-destructive">{errorMsg}</p>
      )}
    </div>
  );
}
